import { Box, Card, CardContent, GridLegacy, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import StarIcon from '@mui/icons-material/Star';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getPageTitle } from 'src/config/projectConfig';

const tabs = [
  { label: 'Pro Backup', color: '#38B2AC' },
  { label: 'PC Backup', color: '#F56565' },
  { label: 'VM Backup', color: '#4299E1' },
];

const levelPrices = [
  { range: '0 - 500 GB', price: '\u20AC 0.50' },
  { range: '501 - 750 GB', price: '\u20AC 0.49' },
  { range: '751 - 1000 GB', price: '\u20AC 0.48' },
  { range: '1001 - 1250 GB', price: '\u20AC 0.47' },
  { range: '1251 - 1500 GB', price: '\u20AC 0.46' },
  { range: '1501 > GB', price: '\u20AC 0.45' },
];

const optionalModules = [
  { name: 'M365 backup', price: '\u20AC 0.50' },
  { name: 'Google workspace', price: '\u20AC 0.50' },
  { name: 'Exchange mailbox', price: '\u20AC 0.95' },
  { name: 'VMware guest', price: '\u20AC 4.00' },
  { name: 'VMware socket', price: '\u20AC 25.00' },
  { name: 'Hyper-V guest', price: '\u20AC 4.00' },
  { name: 'Hyper-V socket', price: '\u20AC 25.00' },
  { name: 'Synology NAS Backup', price: '\u20AC 3.50' },
  { name: 'QNAP NAS Backup', price: '\u20AC 3.50' },
  { name: 'Granular Restore / OpenDirect', price: '\u20AC 1.00' },
];

const includedModules = [
  'Windows System Backup',
  'Continuous Data Protection',
  'Lotus Notes',
  'Deduplications',
  'Lotus Domino',
  'MySQL Database Server',
  'MariaDB Database Server',
  'Microsoft SQL Server',
  'Tibero Database Server',
  'PostgreSQL',
];

const packages = [
  { name: 'Pro Backup Demo', purchase: '\u20AC 0.50', recommended: '\u20AC 0.50' },
  { name: 'Pro 2 GB', purchase: '\u20AC 0.50', recommended: '\u20AC 0.50' },
  { name: 'Pro 5 GB', purchase: '\u20AC 0.49', recommended: '\u20AC 0.49' },
  { name: 'Pro 10 GB', purchase: '\u20AC 0.48', recommended: '\u20AC 0.48' },
  { name: 'Pro 20 GB', purchase: '\u20AC 0.47', recommended: '\u20AC 0.47' },
  { name: 'Pro 30 GB', purchase: '\u20AC 0.46', recommended: '\u20AC 0.46' },
  { name: 'Pro 50 GB', purchase: '\u20AC 0.45', recommended: '\u20AC 0.45' },
  { name: 'Pro 75 GB', purchase: '\u20AC 0.50', recommended: '\u20AC 0.50' },
  { name: 'Pro 100 GB', purchase: '\u20AC 0.50', recommended: '\u20AC 0.49' },
  { name: 'Pro 150 GB', purchase: '\u20AC 0.48', recommended: '\u20AC 0.48' },
  { name: 'Pro 200 GB', purchase: '\u20AC 0.47', recommended: '\u20AC 0.47' },
  { name: 'Pro 250 GB', purchase: '\u20AC 0.46', recommended: '\u20AC 0.46' },
  { name: 'Pro 300 GB', purchase: '\u20AC 0.45', recommended: '\u20AC 0.45' },
  { name: 'Pro 350 GB', purchase: '\u20AC 0.50', recommended: '\u20AC 0.50' },
  { name: 'Pro 400 GB', purchase: '\u20AC 0.49', recommended: '\u20AC 0.49' },
  { name: 'Pro 450 GB', purchase: '\u20AC 0.48', recommended: '\u20AC 0.48' },
];

const Prices = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Price Book')}</title>
      </Helmet>

      <Box p={3}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Price Book
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Prices are per month and exclusive VAT
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 3 }}
        >
          {tabs.map((t) => (
            <Tab
              key={t.label}
              label={
                <Box display="flex" alignItems="center" gap={0.75}>
                  <FolderIcon sx={{ fontSize: 16, color: t.color }} />
                  {t.label}
                </Box>
              }
              sx={{ textTransform: 'none', fontWeight: 500, fontSize: 13, minHeight: 48 }}
            />
          ))}
        </Tabs>

        {/* Top row: Description, Optional, Included */}
        <GridLegacy container spacing={3}>
          <GridLegacy item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Description
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight={600} fontSize={13}>
                    V10 License cost per device
                  </Typography>
                  <Typography variant="body2" fontSize={13}>
                    {'\u20AC'} 3.50
                  </Typography>
                </Box>
                <Box display="flex" gap={3} mb={2}>
                  <Typography variant="caption" color="textSecondary" fontSize={11}>
                    Payment Model: {'\u20AC'}Level system
                  </Typography>
                  <Typography variant="caption" color="textSecondary" fontSize={11}>
                    Payment interval: 3 Months
                  </Typography>
                </Box>

                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Level Price per GB
                </Typography>
                {levelPrices.map((lp) => (
                  <Box
                    key={lp.range}
                    display="flex"
                    justifyContent="space-between"
                    py={0.75}
                    borderBottom="1px solid #EDF2F7"
                  >
                    <Typography variant="body2" fontSize={12}>{lp.range}</Typography>
                    <Typography variant="body2" fontSize={12} fontWeight={500}>{lp.price}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </GridLegacy>

          <GridLegacy item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Optional Modules
                </Typography>
                {optionalModules.map((om) => (
                  <Box
                    key={om.name}
                    display="flex"
                    justifyContent="space-between"
                    py={0.75}
                    borderBottom="1px solid #EDF2F7"
                  >
                    <Typography variant="body2" fontSize={12}>{om.name}</Typography>
                    <Typography variant="body2" fontSize={12} fontWeight={500}>{om.price}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </GridLegacy>

          <GridLegacy item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Included Modules
                </Typography>
                {includedModules.map((m) => (
                  <Box
                    key={m}
                    display="flex"
                    justifyContent="space-between"
                    py={0.75}
                    borderBottom="1px solid #EDF2F7"
                  >
                    <Typography variant="body2" fontSize={12}>{m}</Typography>
                    <Typography variant="body2" fontSize={12} color="textSecondary">{m}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </GridLegacy>
        </GridLegacy>

        {/* Packages table */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Packages
            </Typography>
            <Typography variant="body2" fontSize={12} color="textSecondary" mb={2}>
              Pro Backup Purchase Price: {'\u20AC'} 0.50/GB {'\u00B7'} 1x {'\u20AC'} 3.50/device
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#4A5568' }}>
                    Package
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#4A5568' }}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Purchase Price
                      <StarIcon sx={{ fontSize: 14, color: '#ECC94B' }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#4A5568' }}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Recommended selling price
                      <StarIcon sx={{ fontSize: 14, color: '#ECC94B' }} />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg) => (
                  <TableRow key={pkg.name} hover>
                    <TableCell sx={{ fontSize: 12 }}>{pkg.name}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{pkg.purchase}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{pkg.recommended}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default Prices;
